import { useCallback, useEffect, useState } from 'react';

import type { MicrophoneDevice } from '../components/MicrophoneSelector';

interface UseMicrophoneOptions {
  /** Audio constraints for getUserMedia */
  audioConstraints?: MediaTrackConstraints;
  /** Whether to automatically access microphone on mount */
  autoAccess?: boolean;
  /** Selected device ID */
  deviceId?: string;
}

interface UseMicrophoneReturn {
  /** Current media stream */
  stream: MediaStream | null;
  /** Whether microphone is currently active */
  isActive: boolean;
  /** Error message if any */
  error: string | null;
  /** Available microphone devices */
  devices: MicrophoneDevice[];
  /** Currently selected device ID */
  selectedDeviceId: string;
  /** Access microphone with optional device selection */
  accessMicrophone: (deviceId?: string) => Promise<MediaStream>;
  /** Free microphone and stop all tracks */
  freeMicrophone: () => void;
  /** Set selected device ID */
  setSelectedDeviceId: (deviceId: string) => void;
  /** Refresh available devices */
  refreshDevices: () => Promise<void>;
}

/**
 * Custom hook for microphone management
 *
 * @param options - Hook options
 * @returns Microphone management functions and state
 */
const useMicrophone = (options: UseMicrophoneOptions = {}): UseMicrophoneReturn => {
  const {
    audioConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 48000,
      channelCount: 1,
    },
    autoAccess = false,
    deviceId: initialDeviceId,
  } = options;

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MicrophoneDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(initialDeviceId || '');

  /**
   * Load available audio input devices
   */
  const loadAudioDevices = useCallback(async () => {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = mediaDevices
        .filter((device) => device.kind === 'audioinput' && device.deviceId)
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}...`,
          groupId: device.groupId,
        }));

      setDevices(audioInputs);
    } catch (err) {
      console.error('[useMicrophone] Error loading audio devices:', err);
      setError('Failed to load audio devices');
    }
  }, []);

  /**
   * Access microphone with optional device selection
   */
  const accessMicrophone = useCallback(
    async (deviceId?: string): Promise<MediaStream> => {
      try {
        setError(null);

        const targetDeviceId = deviceId || selectedDeviceId;
        const constraints = {
          ...audioConstraints,
          ...(targetDeviceId ? { deviceId: { exact: targetDeviceId } } : {}),
        };

        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: constraints,
        });

        setStream(newStream);
        setIsActive(true);
        setSelectedDeviceId(targetDeviceId);

        // Update device list after getting permission
        await loadAudioDevices();

        return newStream;
      } catch (err) {
        console.error('[useMicrophone] Error accessing microphone:', err);
        setIsActive(false);

        if (err instanceof Error) {
          if (err.name === 'NotAllowedError') {
            // Check if it's a permissions policy violation
            if (
              err.message.includes('Permissions policy violation') ||
              err.message.includes('microphone is not allowed')
            ) {
              setError(
                'Microphone access blocked by browser security policy. Please contact support if this persists.'
              );
            } else {
              setError('Microphone access denied by user');
            }
          } else if (err.name === 'NotFoundError') {
            setError('No microphone found on device');
          } else if (err.name === 'NotSupportedError') {
            setError('Microphone access not supported in this browser');
          } else {
            setError(err.message);
          }
        } else {
          setError('Unknown error accessing microphone');
        }

        throw err; // Re-throw the error so the caller can handle it
      }
    },
    [audioConstraints, selectedDeviceId, loadAudioDevices]
  );

  /**
   * Free microphone and stop all tracks
   */
  const freeMicrophone = useCallback(() => {
    if (stream) {
      try {
        const tracks = stream.getTracks();

        tracks.forEach((track) => {
          track.stop();
        });
      } catch (err) {
        console.error('[useMicrophone] Error stopping local stream tracks:', err);
        setError('Error stopping microphone tracks');
      }
      setStream(null);
      setIsActive(false);
    }
  }, [stream]);

  /**
   * Refresh available devices
   */
  const refreshDevices = useCallback(async () => {
    await loadAudioDevices();
  }, [loadAudioDevices]);

  // Load devices on mount
  useEffect(() => {
    loadAudioDevices().catch((e) => console.error(e));
  }, [loadAudioDevices]);

  // Auto-access microphone if enabled
  useEffect(() => {
    if (autoAccess && !isActive && devices.length > 0) {
      accessMicrophone().catch((e) => console.error(e));
    }
  }, [autoAccess, isActive, devices.length, accessMicrophone]);

  // Set up device change listener
  useEffect(() => {
    const handleDeviceChange = () => {
      loadAudioDevices().catch((e) => console.error(e));
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [loadAudioDevices]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, [stream]);

  // Additional cleanup when page is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };

    const handlePageHide = () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [stream]);

  return {
    stream,
    isActive,
    error,
    devices,
    selectedDeviceId,
    accessMicrophone,
    freeMicrophone,
    setSelectedDeviceId,
    refreshDevices,
  };
};

export default useMicrophone;
